/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { isAuthenticatedUser } from 'js/lib/user';

import { Button, Fade, InlineNotification } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import { getContinuousChatNotificationConfig } from 'bundles/ai-coach-platform/components/patterns/chat/notifications/configs/ContinuousChatNotificationConfig';
import { useMemoryPreferenceNotification } from 'bundles/ai-coach-platform/components/patterns/chat/notifications/configs/MemoryPreferenceNotificationConfig';
import { getPrivacyNotificationConfig } from 'bundles/ai-coach-platform/components/patterns/chat/notifications/configs/PrivacyNotificationConfig';
import type {
  MemoryNotificationConfig,
  NotificationConfig,
} from 'bundles/ai-coach-platform/components/patterns/chat/notifications/types';
import type { SuggestionAction as SuggestionActionType } from 'bundles/ai-coach-platform/components/patterns/suggestions/types';
import { ResolutionStatus } from 'bundles/ai-coach-platform/components/patterns/suggestions/types';

import _t from 'i18n!nls/ai-coach-platform';

const styles = {
  root: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-100);
    padding: 0 var(--cds-spacing-200);
    max-width: 770px;
    margin: 0 auto var(--cds-spacing-200);
  `,
  actions: css`
    display: flex;
  `,
};

type Props = {
  onAllResolved?: () => void;
  memoryNotificationConfig?: MemoryNotificationConfig;
  enableMemory?: boolean;
  brandLabel?: string;
};

type UseChatNotificationsReturn = {
  notifications: NotificationConfig[];
  handleAction: (notificationId: string, action: SuggestionActionType) => void;
  allResolved: boolean;
};

type UseChatNotificationsParams = {
  onAllResolved?: () => void;
  memoryNotificationConfig?: MemoryNotificationConfig;
  enableMemory?: boolean;
};

const useChatNotifications = ({
  onAllResolved,
  memoryNotificationConfig,
  enableMemory,
}: UseChatNotificationsParams): UseChatNotificationsReturn => {
  const [notifications, setNotifications] = React.useState<NotificationConfig[]>([]);
  const initializedRef = React.useRef(false);
  const [allResolved, setAllResolved] = React.useState(false);
  // this notification needs a hook since it has a mutation
  const memoryNotification = useMemoryPreferenceNotification(memoryNotificationConfig, _t('Coursera AI Chat'));

  const track = useTracker();

  const handleAction = React.useCallback<(notificationId: string, action: SuggestionActionType) => void>(
    (notificationId, action) => {
      setNotifications((prevNotifications) =>
        prevNotifications.map((notification) => {
          if (notification.id === notificationId) {
            const newStatus = action === 'accept' ? ResolutionStatus.ACCEPTED : ResolutionStatus.REJECTED;
            return {
              ...notification,
              status: newStatus,
            };
          }
          return notification;
        })
      );
    },
    []
  );

  React.useEffect(() => {
    // Only initialize notifications once on mount
    if (!initializedRef.current) {
      initializedRef.current = true;

      const isLoggedInUser = isAuthenticatedUser();

      // Initialize notifications from imported configurations,
      // filter out those that are dismissed already, and the memory
      // notification when the consumer has opted out.
      const candidates = enableMemory
        ? [memoryNotification, getContinuousChatNotificationConfig()]
        : [getContinuousChatNotificationConfig()];
      const allNotifications = (isLoggedInUser ? candidates : [getPrivacyNotificationConfig()]).filter(
        (notification) => !notification.isDismissed
      );

      setNotifications(allNotifications);

      allNotifications.forEach((notification) => {
        notification.onMount?.();

        track('view_notification', {
          notification: {
            templateId: notification.id,
          },
        });
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Only run once on mount to avoid infinite loop

  React.useEffect(() => {
    const allNotifsResolved = notifications.every(
      (notification) => notification.status !== ResolutionStatus.UNRESOLVED
    );
    let timeout: NodeJS.Timeout;

    if (allNotifsResolved && notifications.length > 0) {
      // delay resetting so learners can see the resolved state for a bit
      timeout = setTimeout(() => {
        setAllResolved(true);
        onAllResolved?.();
      }, 500);
    }

    return () => {
      clearTimeout(timeout);
    };
  }, [notifications, onAllResolved]);

  return {
    notifications,
    handleAction,
    allResolved,
  };
};

export const ChatNotifications = (props: Props): React.ReactElement | null => {
  const { onAllResolved, memoryNotificationConfig, enableMemory = true } = props;
  const { notifications, handleAction, allResolved } = useChatNotifications({
    onAllResolved,
    memoryNotificationConfig,
    enableMemory,
  });
  const track = useTracker();

  const handleNotificationAction = React.useCallback<
    (notification: NotificationConfig, action: SuggestionActionType) => void
  >(
    (notification, action) => {
      handleAction(notification.id, action);
      notification.onAction?.(action);

      track('click_notification', {
        notification: {
          templateId: notification.id,
        },
      });
    },
    [handleAction, track]
  );

  if (notifications.length === 0) {
    return null;
  }

  const unresolvedNotifications = notifications.filter((n) => n.status === ResolutionStatus.UNRESOLVED);

  return (
    <Fade in={unresolvedNotifications.length > 0 && !allResolved} timeout={300}>
      <div className="coach-chat-notifications" css={styles.root}>
        {unresolvedNotifications.map((notification) => (
          <div key={notification.id} data-testid={`chat-notification-${notification.id}`}>
            <InlineNotification
              severity="information"
              title={notification.title}
              titleComponent="h2"
              actions={
                <div css={styles.actions}>
                  {notification.acceptLabel && (
                    <Button
                      size="small"
                      variant="primary"
                      data-testid={`accept-${notification.id}`}
                      onClick={() => handleNotificationAction(notification, 'accept')}
                    >
                      {notification.acceptLabel}
                    </Button>
                  )}
                  {!notification.hideDismiss && notification.dismissLabel && (
                    <Button
                      size="small"
                      variant="ghostSecondary"
                      data-testid={`reject-${notification.id}`}
                      onClick={() => handleNotificationAction(notification, 'reject')}
                    >
                      {notification.dismissLabel}
                    </Button>
                  )}
                </div>
              }
            >
              <>{notification.content}</>
            </InlineNotification>
          </div>
        ))}
      </div>
    </Fade>
  );
};

export default ChatNotifications;
